import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  deliveryDashboardDataAtom,
  deliveryDashboardStatusAtom,
  giftDeliverySearchDataAtomFamily,
  giftDeliverySearchStatusAtomFamily,
  giftDeliveryStatisticsDataAtom,
} from '@shared/api/giftDelivery/giftDelivery.atom';
import {
  UpdateDeliveryStatusDto,
  updateGiftDeliveryRecipient,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  deliveryDashboardUrl,
  giftDeliveryDeliveredKey,
  giftDeliveryDeliveredUrl,
  giftDeliveryStatisticsUrl,
  useDeliveryDashboardHook,
  useGiftDeliveryDeliveredHook,
  useGiftDeliveryStatisticsHook,
} from '@shared/api/giftDelivery/giftDelivery.hook';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
  GiftDeliveryType,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { postImage } from '@shared/api/uploadImage';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';

import { DeliveredDeliveryTable } from '../components/DeliveredDeliveryTable';
import { DeliveredEditModal } from '../components/DeliveredEditModal';
import { ImagePreviewModal } from '../components/ImagePreviewModal';
import { RecipientEditModal } from '../components/RecipientEditModal';
import { StatusChangeModal } from '../components/StatusChangeModal';
import { Toast } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import {
  nextStatusLabel,
  nextStatusMap,
  prevStatusLabel,
  prevStatusMap,
  statusLabel,
  typeBadgeStyle,
  typeLabel,
} from '../utils/delivery.constants';
import {
  EditModalState,
  ImageModalState,
  RecipientModalState,
  StatusModalState,
} from '../utils/delivery.types';
import {
  formatDate,
  formatDateTime,
  formatPhone,
  getImageUrl,
  getItemName,
  getOrdererInfo,
  toLocalDateTimeString,
} from '../utils/delivery.utils';

export const GiftDeliveryManagementPage: FC = function GiftDeliveryManagementPage() {
  useDeliveryDashboardHook();
  useGiftDeliveryStatisticsHook();

  const queryClient = useQueryClient();
  const dashboardStatus = useRecoilValue(deliveryDashboardStatusAtom);
  const dashboard = useRecoilValue(deliveryDashboardDataAtom);
  const statistics = useRecoilValue(giftDeliveryStatisticsDataAtom);

  const invalidateDashboard = useCallback(async () => {
    await queryClient.invalidateQueries(deliveryDashboardUrl);
    await queryClient.invalidateQueries(giftDeliveryStatisticsUrl);
    await queryClient.invalidateQueries(giftDeliveryDeliveredUrl(LowerRole.Admin));
  }, [queryClient]);

  const activeDeliveries = useMemo(() => {
    if (!dashboard) return [];
    return [
      ...(dashboard.waitingAddress ?? []),
      ...(dashboard.addressSubmitted ?? []),
      ...(dashboard.preparing ?? []),
      ...(dashboard.shipping ?? []),
    ];
  }, [dashboard]);
  const todayDelivered = useMemo(() => dashboard?.todayDelivered ?? [], [dashboard]);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showTodayProcessed, setShowTodayProcessed] = useState(false);

  const { message: toastMessage, show: showToast, durationMs: toastDurationMs } =
    useToast(1200);

  const { assetRole } = useAuth();

  // 배송완료 필터 선택 시에만 전체 배송완료 내역을 조회한다 (그 외엔 skip)
  const showDeliveredView = statusFilter === GiftDeliveryStatus.Delivered;
  useGiftDeliveryDeliveredHook(LowerRole.Admin, !showDeliveredView);
  const deliveredAll = useRecoilValue(
    giftDeliverySearchDataAtomFamily(giftDeliveryDeliveredKey(LowerRole.Admin)),
  );
  const deliveredStatus = useRecoilValue(
    giftDeliverySearchStatusAtomFamily(giftDeliveryDeliveredKey(LowerRole.Admin)),
  );
  const filteredDelivered = useMemo(() => {
    let result = [...deliveredAll];
    if (typeFilter !== 'all') {
      result = result.filter((d) => d.type === typeFilter);
    }
    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((d) => {
        const recipientPhone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const recipientName = d.recipient?.name?.toLowerCase() || '';
        const itemName = getItemName(d).toLowerCase();
        const address = d.recipient?.address?.toLowerCase() || '';
        return (
          recipientPhone.includes(search) ||
          recipientName.includes(search) ||
          itemName.includes(search) ||
          address.includes(search)
        );
      });
    }
    return result;
  }, [deliveredAll, typeFilter, searchText]);

  // 상태 변경 모달
  const [statusModal, setStatusModal] = useState<StatusModalState | null>(null);

  const [recipientModal, setRecipientModal] = useState<RecipientModalState | null>(null);

  const [imageModal, setImageModal] = useState<ImageModalState | null>(null);

  const [editModal, setEditModal] = useState<EditModalState | null>(null);

  const [revertingId, setRevertingId] = useState<string | null>(null);

  const handleRevertStatus = useCallback(
    async (deliveryId: string, currentStatus: GiftDeliveryStatus) => {
      const prev = prevStatusMap[currentStatus];
      if (!prev) return;
      const label = prevStatusLabel[currentStatus] || '이전 상태로 되돌리기';
      if (!confirm(`${label} 하시겠습니까?`)) return;
      setRevertingId(deliveryId);
      try {
        await updateGiftDeliveryStatus(deliveryId, prev);
        await invalidateDashboard();
        alert('상태가 되돌려졌습니다.');
      } catch (error) {
        console.error('상태 되돌리기 실패:', error);
        alert('상태 되돌리기에 실패했습니다.');
      } finally {
        setRevertingId(null);
      }
    },
    [invalidateDashboard],
  );

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  const openEditModal = useCallback((delivery: GiftDeliveryForAdmin) => {
    const deliveredImg = delivery.shipping?.deliveredImage;
    const existingUrl =
      deliveredImg && typeof deliveredImg === 'object'
        ? fileBucketUrl(deliveredImg.url, 'w400')
        : null;
    setEditModal({
      deliveryId: delivery._id,
      itemName: getItemName(delivery),
      address: delivery.recipient?.address || '-',
      deliveredAt: delivery.shipping?.deliveredAt
        ? toLocalDateTimeString(new Date(delivery.shipping.deliveredAt))
        : toLocalDateTimeString(),
      imageFile: null,
      imagePreview: null,
      existingImageUrl: existingUrl,
      isSaving: false,
      showRevertConfirm: false,
      revertConfirmText: '',
    });
  }, []);

  const handleRevertDelivered = useCallback(async () => {
    if (!editModal) return;
    setEditModal((prev) => (prev ? { ...prev, isSaving: true } : null));
    try {
      await updateGiftDeliveryStatus(editModal.deliveryId, GiftDeliveryStatus.Shipping);
      await invalidateDashboard();
      setEditModal(null);
      alert('배송중 상태로 원복되었습니다.');
    } catch (error) {
      console.error('원복 실패:', error);
      alert('원복에 실패했습니다.');
      setEditModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [editModal, invalidateDashboard]);

  const handleSaveEdit = useCallback(async () => {
    if (!editModal) return;
    setEditModal((prev) => (prev ? { ...prev, isSaving: true } : null));

    try {
      let deliveredImageId: string | undefined;
      if (editModal.imageFile) {
        const img = await postImage(editModal.imageFile, assetRole());
        deliveredImageId = img._id;
      }

      await updateGiftDeliveryStatus(editModal.deliveryId, GiftDeliveryStatus.Delivered, {
        deliveredAt: new Date(editModal.deliveredAt).toISOString(),
        ...(deliveredImageId ? { deliveredImage: deliveredImageId } : {}),
      });
      await invalidateDashboard();
      setEditModal(null);
      alert('수정이 완료되었습니다.');
    } catch (error) {
      console.error('수정 실패:', error);
      alert('수정에 실패했습니다.');
      setEditModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [editModal, assetRole, invalidateDashboard]);

  const handleSaveRecipient = useCallback(async () => {
    if (!recipientModal) return;
    setRecipientModal((prev) => (prev ? { ...prev, isSaving: true } : null));
    try {
      await updateGiftDeliveryRecipient(recipientModal.deliveryId, {
        name: recipientModal.name || undefined,
        phoneNumber: recipientModal.phoneNumber || undefined,
        postalCode: recipientModal.postalCode || undefined,
        address: recipientModal.address || undefined,
        addressDetail: recipientModal.addressDetail || undefined,
      });
      await invalidateDashboard();
      setRecipientModal(null);
      alert('수령인 정보가 수정되었습니다.');
    } catch (error) {
      console.error('수령인 정보 수정 실패:', error);
      alert('수정에 실패했습니다.');
      setRecipientModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [recipientModal, invalidateDashboard]);

  const openStatusModal = useCallback(
    (deliveryId: string) => {
      const delivery = activeDeliveries.find((d) => d._id === deliveryId);
      if (!delivery) return;
      const next = nextStatusMap[delivery.deliveryStatus];
      if (!next) return;
      setStatusModal({
        deliveryIds: [deliveryId],
        currentStatus: delivery.deliveryStatus,
        nextStatus: next,
        trackingNumber: '',
        carrier: '',
        shippedAt: toLocalDateTimeString(),
        deliveredAt: toLocalDateTimeString(),
        deliveredImageFile: null,
        deliveredImagePreview: null,
        isBulk: false,
        showRevert: false,
        revertConfirmText: '',
        existingCarrier: delivery.shipping?.carrier || '',
        existingTrackingNumber: delivery.shipping?.trackingNumber || '',
        existingShippedAt: delivery.shipping?.shippedAt
          ? toLocalDateTimeString(new Date(delivery.shipping.shippedAt))
          : '',
      });
    },
    [activeDeliveries],
  );

  const openBulkStatusModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const firstDelivery = activeDeliveries.find((d) => d._id === targetIds[0]);
    if (!firstDelivery) return;
    const next = nextStatusMap[firstDelivery.deliveryStatus];
    if (!next) {
      alert('상태를 변경할 수 없는 항목이 포함되어 있습니다.');
      return;
    }
    setStatusModal({
      deliveryIds: targetIds,
      currentStatus: firstDelivery.deliveryStatus,
      nextStatus: next,
      trackingNumber: '',
      carrier: '',
      shippedAt: toLocalDateTimeString(),
      deliveredAt: toLocalDateTimeString(),
      deliveredImageFile: null,
      deliveredImagePreview: null,
      isBulk: true,
      showRevert: false,
      revertConfirmText: '',
      existingCarrier: '',
      existingTrackingNumber: '',
      existingShippedAt: '',
    });
  }, [selectedIds, activeDeliveries]);

  const handleConfirmStatusChange = useCallback(async () => {
    if (!statusModal) return;

    // 2단계: 준비중 → 배송중 (송장 정보)
    const shipping: UpdateDeliveryStatusDto['shipping'] =
      statusModal.nextStatus === GiftDeliveryStatus.Shipping
        ? {
            trackingNumber: statusModal.trackingNumber || undefined,
            carrier: statusModal.carrier || undefined,
            shippedAt: statusModal.shippedAt
              ? new Date(statusModal.shippedAt).toISOString()
              : new Date().toISOString(),
          }
        : statusModal.nextStatus === GiftDeliveryStatus.Delivered
        ? {
            deliveredAt: statusModal.deliveredAt
              ? new Date(statusModal.deliveredAt).toISOString()
              : new Date().toISOString(),
          }
        : undefined;

    // 3단계: 배송중 → 배송완료 (사진 업로드 → shipping.deliveredImage에 포함)
    if (
      statusModal.nextStatus === GiftDeliveryStatus.Delivered &&
      statusModal.deliveredImageFile
    ) {
      try {
        const img = await postImage(statusModal.deliveredImageFile, assetRole());
        if (shipping) {
          shipping.deliveredImage = img._id;
        }
      } catch {
        alert('이미지 업로드에 실패했습니다.');
        return;
      }
    }

    if (statusModal.isBulk) {
      setIsBulkProcessing(true);
      setStatusModal(null);
      let successCount = 0;
      let failCount = 0;

      for (const id of statusModal.deliveryIds) {
        try {
          await updateGiftDeliveryStatus(id, statusModal.nextStatus, shipping);
          successCount++;
        } catch (error) {
          console.error(`상태 변경 실패 (${id}):`, error);
          failCount++;
        }
      }

      setIsBulkProcessing(false);
      setSelectedIds(new Set());
      await invalidateDashboard();

      if (failCount === 0) {
        alert(`${successCount}건 상태 변경 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    } else {
      const id = statusModal.deliveryIds[0] ?? '';
      setProcessingId(id);
      setStatusModal(null);

      try {
        await updateGiftDeliveryStatus(id, statusModal.nextStatus, shipping);
        await invalidateDashboard();
        alert('상태 변경이 완료되었습니다.');
      } catch (error) {
        console.error('상태 변경 실패:', error);
        alert('상태 변경에 실패했습니다.');
      } finally {
        setProcessingId(null);
      }
    }
  }, [statusModal, assetRole, invalidateDashboard]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const filteredData = useMemo(() => {
    let result = [...activeDeliveries];

    if (statusFilter !== 'all') {
      result = result.filter((d) => d.deliveryStatus === statusFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter((d) => d.type === typeFilter);
    }

    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((d) => {
        const recipientPhone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const recipientName = d.recipient?.name?.toLowerCase() || '';
        const itemName = getItemName(d).toLowerCase();
        const address = d.recipient?.address?.toLowerCase() || '';
        return (
          recipientPhone.includes(search) ||
          recipientName.includes(search) ||
          itemName.includes(search) ||
          address.includes(search)
        );
      });
    }

    return result;
  }, [activeDeliveries, statusFilter, typeFilter, searchText]);

  const isAllSelected = useMemo(
    () => filteredData.length > 0 && filteredData.every((d) => selectedIds.has(d._id)),
    [filteredData, selectedIds],
  );

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map((d) => d._id)));
    }
  }, [isAllSelected, filteredData]);

  if (dashboardStatus === 'loading' || dashboardStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div>
          <h1 className="text-xl font-bold">배송 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            배송 상태별 현황을 확인하고 관리하세요.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 p-4 lg:grid-cols-6">
        {[
          {
            key: GiftDeliveryStatus.WaitingAddress,
            value: statistics?.waitingAddress,
            label: '주소입력대기',
            bg: 'bg-amber-50',
            activeBg: 'ring-2 ring-amber-400 bg-amber-100',
            text: 'text-amber-700',
            sub: 'text-amber-600',
          },
          {
            key: GiftDeliveryStatus.AddressSubmitted,
            value: statistics?.addressSubmitted ?? activeDeliveries.length,
            label: '주소입력완료',
            bg: 'bg-blue-50',
            activeBg: 'ring-2 ring-blue-400 bg-blue-100',
            text: 'text-blue-700',
            sub: 'text-blue-600',
          },
          {
            key: GiftDeliveryStatus.Preparing,
            value: statistics?.preparing,
            label: '준비중',
            bg: 'bg-indigo-50',
            activeBg: 'ring-2 ring-indigo-400 bg-indigo-100',
            text: 'text-indigo-700',
            sub: 'text-indigo-600',
          },
          {
            key: GiftDeliveryStatus.Shipping,
            value: statistics?.shipping,
            label: '배송중',
            bg: 'bg-purple-50',
            activeBg: 'ring-2 ring-purple-400 bg-purple-100',
            text: 'text-purple-700',
            sub: 'text-purple-600',
          },
          {
            key: GiftDeliveryStatus.Delivered,
            value: statistics?.delivered,
            label: '배송완료',
            bg: 'bg-green-50',
            activeBg: 'ring-2 ring-green-400 bg-green-100',
            text: 'text-green-700',
            sub: 'text-green-600',
          },
          {
            key: 'all',
            value: statistics?.total,
            label: '전체',
            bg: 'bg-gray-100',
            activeBg: 'ring-2 ring-gray-400 bg-gray-200',
            text: 'text-gray-700',
            sub: 'text-gray-600',
          },
        ].map((card) => (
          <button
            key={card.key}
            onClick={() => setStatusFilter(card.key)}
            className={`rounded-lg p-4 text-center transition-all ${
              statusFilter === card.key ? card.activeBg : card.bg
            } cursor-pointer hover:opacity-80`}
          >
            <p className={`text-2xl font-bold ${card.text}`}>{card.value ?? '-'}</p>
            <p className={`text-sm ${card.sub}`}>{card.label}</p>
          </button>
        ))}
      </div>

      {/* 오늘 처리 내역 토글 (배송완료 필터 선택 시엔 아래 전체 목록으로 대체) */}
      {!showDeliveredView && todayDelivered.length > 0 && (
        <div className="px-4 pb-2">
          <button
            onClick={() => setShowTodayProcessed((prev) => !prev)}
            className="flex w-full items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-left transition-colors hover:bg-green-100"
          >
            <span className="text-sm font-medium text-green-800">
              오늘 배송완료 ({todayDelivered.length}건)
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-green-600 transition-transform ${
                showTodayProcessed ? 'rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showTodayProcessed && (
            <div className="mt-2">
              <DeliveredDeliveryTable
                deliveries={todayDelivered}
                onOpenImage={setImageModal}
                onOpenEdit={openEditModal}
              />
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value={GiftDeliveryStatus.WaitingAddress}>주소입력대기</option>
            <option value={GiftDeliveryStatus.AddressSubmitted}>주소입력완료</option>
            <option value={GiftDeliveryStatus.Preparing}>준비중</option>
            <option value={GiftDeliveryStatus.Shipping}>배송중</option>
            <option value={GiftDeliveryStatus.Delivered}>배송완료</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 타입</option>
            <option value={GiftDeliveryType.Individual}>개별배송</option>
            <option value={GiftDeliveryType.Bulk}>일괄배송</option>
          </select>

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="수령인, 상품명, 주소 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {!showDeliveredView && selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={openBulkStatusModal}
              disabled={isBulkProcessing}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBulkProcessing ? '처리 중...' : '일괄 상태 변경'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              선택 해제
            </button>
            <button
              onClick={() => {
                const rows = filteredData.filter((d) => selectedIds.has(d._id));
                if (rows.length === 0) {
                  alert('선택된 항목이 없습니다.');
                  return;
                }
                const esc = (v: string | undefined) =>
                  (v || '')
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');
                const dateStr = new Date().toISOString().slice(0, 10);
                const s = (v: string) =>
                  `<Cell ss:StyleID="d"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
                const n = (v: number) =>
                  `<Cell ss:StyleID="d"><Data ss:Type="Number">${v}</Data></Cell>`;
                const h = (v: string) =>
                  `<Cell ss:StyleID="h"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
                const f = (v: string) =>
                  `<Cell ss:StyleID="f"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
                const fn = (v: number) =>
                  `<Cell ss:StyleID="f"><Data ss:Type="Number">${v}</Data></Cell>`;

                let totalQty = 0;
                const dataRows = rows.map((d, idx) => {
                  const o = getOrdererInfo(d);
                  const itm =
                    typeof d.giftItem === 'object'
                      ? (d.giftItem as GiftItemForStore)
                      : null;
                  totalQty += d.quantity || 0;
                  return (
                    '<Row>' +
                    n(idx + 1) +
                    s(d.fullCode || d.code || '') +
                    s(getItemName(d)) +
                    s(itm?.giftItemInfo?.productCode || '') +
                    n(d.quantity || 0) +
                    s(typeLabel[d.type] || d.type) +
                    s(statusLabel[d.deliveryStatus]?.text || d.deliveryStatus) +
                    s(d.recipient?.name || '') +
                    s(d.recipient?.phoneNumber || '') +
                    s(d.recipient?.postalCode || '') +
                    s(d.recipient?.address || '') +
                    s(d.recipient?.addressDetail || '') +
                    s(
                      d.recipient?.submittedAt
                        ? new Date(d.recipient.submittedAt).toLocaleString('ko-KR')
                        : '',
                    ) +
                    s(o.name) +
                    s(o.phone) +
                    '</Row>'
                  );
                });

                const bdr =
                  'Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/></Borders';
                const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Styles>
 <Style ss:ID="Default"><Font ss:FontName="맑은 고딕" ss:Size="9"/></Style>
 <Style ss:ID="t"><Font ss:FontName="맑은 고딕" ss:Size="12" ss:Bold="1"/></Style>
 <Style ss:ID="sub"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Color="#666666"/></Style>
 <Style ss:ID="g"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Bold="1"/><Interior ss:Color="#D9D9D9" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><${bdr}></Style>
 <Style ss:ID="h"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Bold="1"/><Interior ss:Color="#F2F2F2" ss:Pattern="Solid"/><Alignment ss:Vertical="Center"/><${bdr}></Style>
 <Style ss:ID="d"><Font ss:FontName="맑은 고딕" ss:Size="9"/><Alignment ss:Vertical="Center" ss:WrapText="1"/><${bdr}></Style>
 <Style ss:ID="f"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Bold="1"/><Interior ss:Color="#F2F2F2" ss:Pattern="Solid"/><Alignment ss:Vertical="Center"/><Borders><Border ss:Position="Top" ss:LineStyle="Double" ss:Weight="3" ss:Color="#333333"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/></Borders></Style>
</Styles>
<Worksheet ss:Name="배송목록">
<Table>
 <Column ss:Width="35"/><Column ss:Width="100"/><Column ss:Width="140"/><Column ss:Width="80"/>
 <Column ss:Width="45"/><Column ss:Width="60"/><Column ss:Width="70"/><Column ss:Width="70"/>
 <Column ss:Width="100"/><Column ss:Width="55"/><Column ss:Width="180"/><Column ss:Width="100"/>
 <Column ss:Width="115"/><Column ss:Width="70"/><Column ss:Width="100"/>
 <Row ss:Height="24"><Cell ss:StyleID="t"><Data ss:Type="String">배송 목록</Data></Cell></Row>
 <Row ss:Height="16"><Cell ss:StyleID="sub"><Data ss:Type="String">출력일: ${dateStr}   총 ${
                  rows.length
                }건   총 수량: ${totalQty}개</Data></Cell></Row>
 <Row ss:Height="4"/>
 <Row ss:Height="20">
  <Cell ss:StyleID="g"><Data ss:Type="String"></Data></Cell>
  <Cell ss:StyleID="g"><Data ss:Type="String"></Data></Cell>
  <Cell ss:StyleID="g" ss:MergeAcross="4"><Data ss:Type="String">상품 정보</Data></Cell>
  <Cell ss:StyleID="g" ss:MergeAcross="5"><Data ss:Type="String">수령 · 배송 정보</Data></Cell>
  <Cell ss:StyleID="g" ss:MergeAcross="1"><Data ss:Type="String">보내는분</Data></Cell>
 </Row>
 <Row ss:Height="18">
  ${h('No')}${h('코드')}${h('상품명')}${h('상품코드')}${h('수량')}${h('타입')}${h('상태')}
  ${h('수령인')}${h('연락처')}${h('우편번호')}${h('주소')}${h('상세주소')}${h('입력일시')}
  ${h('성함')}${h('연락처')}
 </Row>
 ${dataRows.join('\n ')}
 <Row ss:Height="22">
  ${f('')}${f('')}${f('합계 (' + rows.length + '건)')}${f('')}
  ${fn(totalQty)}
  ${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}
 </Row>
</Table>
</Worksheet>
</Workbook>`;
                const blob = new Blob([xml], {
                  type: 'application/vnd.ms-excel;charset=utf-8',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `배송목록_${dateStr}.xls`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              엑셀 저장 ({selectedIds.size}건)
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        {showDeliveredView ? (
          deliveredStatus === 'loading' || deliveredStatus === 'idle' ? (
            <div className="center-box py-16">
              <Loading />
            </div>
          ) : filteredDelivered.length > 0 ? (
            <DeliveredDeliveryTable
              deliveries={filteredDelivered}
              onOpenImage={setImageModal}
              onOpenEdit={openEditModal}
            />
          ) : (
            <div className="rounded-lg bg-white p-8 text-center shadow">
              <p className="text-gray-500">배송완료된 건이 없습니다.</p>
            </div>
          )
        ) : filteredData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th rowSpan={2} className="w-12 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      disabled={filteredData.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                    />
                  </th>
                  <th
                    rowSpan={2}
                    className="px-3 py-2 text-left text-xs font-medium tracking-wider text-gray-500"
                  >
                    상태
                  </th>
                  <th
                    rowSpan={2}
                    className="px-3 py-2 text-left text-xs font-medium tracking-wider text-gray-500"
                  >
                    코드
                  </th>
                  <th
                    colSpan={3}
                    className="border-l border-gray-200 px-3 py-1.5 text-center text-[10px] font-semibold tracking-wider text-indigo-500"
                  >
                    상품 정보
                  </th>
                  <th
                    colSpan={3}
                    className="border-l border-gray-200 px-3 py-1.5 text-center text-[10px] font-semibold tracking-wider text-emerald-600"
                  >
                    수령 · 배송 정보
                  </th>
                  <th
                    colSpan={2}
                    className="border-l border-gray-200 px-3 py-1.5 text-center text-[10px] font-semibold tracking-wider text-amber-600"
                  >
                    구매 정보
                  </th>
                  <th
                    rowSpan={2}
                    className="border-l border-gray-200 px-3 py-2 text-left text-xs font-medium tracking-wider text-gray-500"
                  >
                    액션
                  </th>
                </tr>
                <tr>
                  <th className="border-l border-gray-200 px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    상품
                  </th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    수량(타입)
                  </th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    상품코드
                  </th>
                  <th className="border-l border-gray-200 px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    수령인
                  </th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    배송지
                  </th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    입력일시
                  </th>
                  <th className="border-l border-gray-200 px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    주문자
                  </th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                    구매일
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredData.map((delivery) => {
                  const ordererInfo = getOrdererInfo(delivery);
                  const isSelected = selectedIds.has(delivery._id);
                  const badge = statusLabel[delivery.deliveryStatus] ?? {
                    text: delivery.deliveryStatus,
                    className: 'bg-gray-100 text-gray-600',
                  };
                  const canChangeStatus = !!nextStatusMap[delivery.deliveryStatus];
                  const itm =
                    typeof delivery.giftItem === 'object'
                      ? (delivery.giftItem as GiftItemForStore)
                      : null;

                  return (
                    <tr
                      key={delivery._id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(delivery._id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>

                      {/* 상태 */}
                      <td className="whitespace-nowrap px-3 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.text}
                        </span>
                      </td>

                      {/* 코드 */}
                      <td className="whitespace-nowrap px-3 py-3">
                        <button
                          onClick={() => copyToClipboard(delivery._id)}
                          className="font-mono text-xs text-gray-600 hover:text-indigo-600"
                          title="클릭하여 _id 복사"
                        >
                          {delivery.fullCode || delivery.code || '-'}
                        </button>
                      </td>

                      {/* 상품 정보 — 상품 */}
                      <td className="border-l border-gray-100 px-3 py-3">
                        <div className="flex items-center gap-2">
                          {getImageUrl(delivery) && (
                            <img
                              src={getImageUrl(delivery)}
                              alt=""
                              className="h-8 w-8 flex-shrink-0 rounded object-cover"
                            />
                          )}
                          <p className="max-w-[140px] truncate text-sm font-medium text-gray-900">
                            {getItemName(delivery)}
                          </p>
                        </div>
                      </td>

                      {/* 상품 정보 — 수량(타입) */}
                      <td className="whitespace-nowrap px-3 py-3">
                        <span className="text-sm text-gray-700">
                          {delivery.quantity}개
                        </span>
                        <span
                          className={`ml-1 rounded-full px-1.5 py-px text-[10px] font-semibold ${
                            typeBadgeStyle[delivery.type] || 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {typeLabel[delivery.type] || delivery.type}
                        </span>
                      </td>

                      {/* 상품 정보 — 상품코드 */}
                      <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-500">
                        {itm?.giftItemInfo?.productCode || '-'}
                      </td>

                      {/* 수령·배송 — 수령인 */}
                      {(() => {
                        const editable =
                          delivery.deliveryStatus === GiftDeliveryStatus.WaitingAddress ||
                          delivery.deliveryStatus === GiftDeliveryStatus.AddressSubmitted;
                        return (
                          <td
                            className={`whitespace-nowrap border-l border-gray-100 px-3 py-3 ${
                              editable ? 'cursor-pointer hover:bg-indigo-50' : ''
                            }`}
                            onClick={
                              editable
                                ? () =>
                                    setRecipientModal({
                                      deliveryId: delivery._id,
                                      name: delivery.recipient?.name || '',
                                      phoneNumber: delivery.recipient?.phoneNumber || '',
                                      postalCode: delivery.recipient?.postalCode || '',
                                      address: delivery.recipient?.address || '',
                                      addressDetail:
                                        delivery.recipient?.addressDetail || '',
                                      isSaving: false,
                                    })
                                : undefined
                            }
                            title={editable ? '클릭하여 수령인 정보 수정' : undefined}
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {delivery.recipient?.name || '-'}
                              {editable && (
                                <span className="ml-1 text-[10px] text-indigo-400">
                                  ✎
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatPhone(delivery.recipient?.phoneNumber)}
                            </p>
                          </td>
                        );
                      })()}

                      {/* 수령·배송 — 배송지 */}
                      {(() => {
                        const editable =
                          delivery.deliveryStatus === GiftDeliveryStatus.WaitingAddress ||
                          delivery.deliveryStatus === GiftDeliveryStatus.AddressSubmitted;
                        return (
                          <td
                            className={`max-w-[200px] px-3 py-3 ${
                              editable ? 'cursor-pointer hover:bg-indigo-50' : ''
                            }`}
                            onClick={
                              editable
                                ? () =>
                                    setRecipientModal({
                                      deliveryId: delivery._id,
                                      name: delivery.recipient?.name || '',
                                      phoneNumber: delivery.recipient?.phoneNumber || '',
                                      postalCode: delivery.recipient?.postalCode || '',
                                      address: delivery.recipient?.address || '',
                                      addressDetail:
                                        delivery.recipient?.addressDetail || '',
                                      isSaving: false,
                                    })
                                : undefined
                            }
                            title={editable ? '클릭하여 배송지 수정' : undefined}
                          >
                            {delivery.recipient?.address ? (
                              <>
                                <p
                                  className="truncate text-xs text-gray-700"
                                  title={`(${delivery.recipient?.postalCode}) ${
                                    delivery.recipient?.address
                                  } ${delivery.recipient?.addressDetail || ''}`}
                                >
                                  ({delivery.recipient?.postalCode}){' '}
                                  {delivery.recipient?.address}
                                </p>
                                {delivery.recipient?.addressDetail && (
                                  <p className="truncate text-xs text-gray-500">
                                    {delivery.recipient.addressDetail}
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">
                                미입력{editable && ' (클릭하여 입력)'}
                              </span>
                            )}
                          </td>
                        );
                      })()}

                      {/* 수령·배송 — 입력일시 */}
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                        {formatDateTime(delivery.recipient?.submittedAt) || '-'}
                      </td>

                      {/* 구매 정보 — 주문자 */}
                      <td className="whitespace-nowrap border-l border-gray-100 px-3 py-3">
                        <p className="text-sm text-gray-900">{ordererInfo.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatPhone(ordererInfo.phone)}
                        </p>
                      </td>

                      {/* 구매 정보 — 구매일 */}
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                        {formatDate(ordererInfo.purchasedAt)}
                      </td>

                      {/* 액션 */}
                      <td className="whitespace-nowrap border-l border-gray-100 px-3 py-3">
                        {canChangeStatus && (
                          <button
                            onClick={() => openStatusModal(delivery._id)}
                            disabled={processingId === delivery._id}
                            className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {processingId === delivery._id
                              ? '처리중...'
                              : nextStatusLabel[delivery.deliveryStatus] || '상태 변경'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <p className="text-gray-500">해당하는 배송 건이 없습니다.</p>
          </div>
        )}
      </div>

      {statusModal && (
        <StatusChangeModal
          modal={statusModal}
          setModal={setStatusModal}
          revertingId={revertingId}
          onConfirm={handleConfirmStatusChange}
          onRevert={handleRevertStatus}
          onSaveShipping={async () => {
            try {
              const targetId = statusModal.deliveryIds[0];
              if (!targetId) return;
              await updateGiftDeliveryStatus(targetId, GiftDeliveryStatus.Shipping, {
                carrier: statusModal.existingCarrier.trim(),
                trackingNumber: statusModal.existingTrackingNumber.trim(),
                shippedAt: statusModal.existingShippedAt
                  ? new Date(statusModal.existingShippedAt).toISOString()
                  : undefined,
              });
              await invalidateDashboard();
              setStatusModal(null);
              alert('송장 정보가 수정되었습니다.');
            } catch {
              alert('송장 수정에 실패했습니다.');
            }
          }}
        />
      )}

      {imageModal && (
        <ImagePreviewModal modal={imageModal} onClose={() => setImageModal(null)} />
      )}

      {editModal && (
        <DeliveredEditModal
          modal={editModal}
          setModal={setEditModal}
          onSave={handleSaveEdit}
          onRevert={handleRevertDelivered}
        />
      )}

      {recipientModal && (
        <RecipientEditModal
          modal={recipientModal}
          setModal={setRecipientModal}
          onSave={handleSaveRecipient}
        />
      )}

      {/* 토스트 */}
      <Toast message={toastMessage} durationMs={toastDurationMs} />
    </div>
  );
};
